import { PopupCustom } from "@components/generals/popup";
import { Spiner } from "@components/loaders/spiner";
import { StudentAbsences } from "@services/dtos/school.dto";
import { SchoolService } from "@services/school.service";
import { useThemeStore } from "@stores/theme.store";
import { useQuery } from "@tanstack/react-query";
import { linkPage } from "@utils/router";
import moment from "moment";
import { useEffect, useState } from "react";
import { BiFilter } from "react-icons/bi";
import { FaAngleRight, FaChevronDown, FaClipboardList } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate } from "react-router-dom";

export const StudentAbsencesPage = () => {
  const navigate = useNavigate();
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);
  const [limit, setLimit] = useState<string>("10");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const { data, isLoading } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_ABSENCES, limit],
    () => SchoolService.studentAbsences({ limit })
  );

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: "Absensi Santri",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ title: "Absensi Santri" });
  }, [setFooter, setHeader, setAppBar]);

  const onOpenDetail = (absences: StudentAbsences) => {
    navigate(linkPage.SCHOOL_STUDENT_ABSENCES_DETAIL, { state: absences });
  };

  return (
    <>
      <div className="card card-style">
        <div className="content mb-0">
          <div className="d-flex justify-content-between align-items-center my-2">
            <p className="mb-0 tw-w-2/3 tw-text-black dark:tw-text-gray-300">
              Jumlah data yang ditampilkan: {limit} data terakhir
            </p>
            <button
              className="tw-text-white tw-bg-gray-700 hover:tw-bg-gray-600 tw-rounded-lg tw-p-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold"
              onClick={() => setPopupFilter(true)}
            >
              <BiFilter size={22} />
            </button>
          </div>

          {isLoading ? (
            <div className="d-flex p-3">
              <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
            </div>
          ) : data?.data != null && data.data.length > 0 ? (
            <div className="list-group list-group-flush">
              {data.data.map((item, idx) => (
                <div
                  key={idx}
                  className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer"
                  onClick={() => onOpenDetail(item)}
                >
                  <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-teal-dark">
                    <FaClipboardList className="m-auto" size={18} />
                  </div>
                  <div className="tw-grow mx-3 my-auto d-flex flex-column tw-w-[calc(100vw-140px)]">
                    <div>
                      {item.kehadiran === "H" && (
                        <span className="badge bg-blue-dark color-white font-10 tw-py-1 px-2 rounded-2 me-2">
                          HADIR
                        </span>
                      )}
                      {item.sakit > 0 && (
                        <span className="badge bg-red-dark color-white font-10 tw-py-1 px-2 rounded-2 me-2">
                          SAKIT : {item.sakit}
                        </span>
                      )}
                      {item.ijin > 0 && (
                        <span className="badge bg-blue-dark color-white font-10 tw-py-1 px-2 rounded-2  me-2">
                          IZIN : {item.ijin}
                        </span>
                      )}
                      {item.alpa > 0 && (
                        <span className="badge bg-magenta-dark color-white font-10 tw-py-1 px-2 rounded-2">
                          ALPHA : {item.alpa}
                        </span>
                      )}
                    </div>
                    <strong className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate">
                      {moment(item.tanggal).format("dddd, DD MMMM YYYY")}
                    </strong>
                  </div>
                  <div className="my-auto">
                    <FaAngleRight
                      className="dark:tw-text-white tw-text-gray-500"
                      size={10}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="tw-p-8 text-center ">
              <FcFinePrint size={50} />
              <div>Data tidak ditemukan</div>
            </div>
          )}
        </div>
      </div>

      <PopupCustom
        show={popupFilter}
        position="bottom"
        height={320}
        onCancel={() => setPopupFilter(false)}
      >
        <div className="pb-2 px-3">
          <h4 className="font-700 mt-3">Filter Data</h4>
          <div className="divider mt-3 mb-3"></div>
          <div className="input-style has-borders input-style-always-active no-icon mb-4">
            <label htmlFor="form5" className="color-highlight font-500">
              Jumlah ditampilkan
            </label>
            <select
              defaultValue="10"
              onChange={(e) => setLimit(e.target.value)}
              className="bg-theme"
            >
              <option value="default" disabled>
                Jumlah ditampilkan
              </option>
              <option value="10">10 Data Terakhir</option>
              <option value="20">20 Data Terakhir</option>
              <option value="50">50 Data Terakhir</option>
              <option value="100">100 Data Terakhir</option>
            </select>
            <span>
              <i>
                <FaChevronDown />
              </i>
            </span>
          </div>
          <div className="divider !tw-my-2" />
          <button
            className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-red-dark tw-grow my-3 tw-w-full tw-h-11"
            onClick={() => setPopupFilter(false)}
          >
            Tutup
          </button>
        </div>
      </PopupCustom>
    </>
  );
};
